import { useEffect, useState } from "react";
import { Plus, Trash2, Check, Minus, Type } from "lucide-react";
import useSettings from "./useSettings";
import { SettingsHeader, Section, Loading } from "./_shared";
import { DEFAULT_BENEFITS } from "../../../data/benefitsDefault";

/**
 * CMS editor for the "Ventajas del Socio" comparison table.
 * Structure (site_settings.benefits_data):
 *   { tiers: [ {key, name_es, name_fr, price, featured} ],
 *     categories: [ {key, label_es, label_fr, rows: [
 *        {label_es, label_fr, cells: [Cell, Cell, Cell]} ]} ] }
 * Cell = { type: "yes"|"no"|"text", es?, fr? }
 */
export default function BenefitsAdmin() {
  const { settings, set, save, saving, saved } = useSettings();
  const [data, setData] = useState(null);

  // Bootstrap local state from settings on first load. Backfills a stable
  // `_cid`/`_rid` on every category/row so React can key the maps correctly
  // (moveCategory/moveRow reorder items — index keys would break inputs).
  useEffect(() => {
    if (settings && data === null) {
      const src = settings.benefits_data && settings.benefits_data.tiers
        ? JSON.parse(JSON.stringify(settings.benefits_data))
        : JSON.parse(JSON.stringify(DEFAULT_BENEFITS));
      const genId = (p) => `${p}-${Math.random().toString(36).slice(2, 10)}`;
      src.categories?.forEach((cat) => {
        if (!cat._cid) cat._cid = genId("c");
        cat.rows?.forEach((row) => { if (!row._rid) row._rid = genId("r"); });
      });
      setData(src);
    }
  }, [settings, data]);

  if (!settings || !data) return <Loading />;

  const commit = (next) => {
    setData(next);
    // Strip local-only keys (_cid/_rid used purely as React keys) before
    // persisting so they never leak into MongoDB.
    const clean = JSON.parse(JSON.stringify(next));
    clean.categories?.forEach((cat) => {
      delete cat._cid;
      cat.rows?.forEach((row) => { delete row._rid; });
    });
    set("benefits_data", clean);
  };
  const clone = (v) => JSON.parse(JSON.stringify(v));

  const resetToDefault = () => {
    if (!window.confirm("¿Restablecer la tabla al valor por defecto? Se perderán tus cambios.")) return;
    commit(clone(DEFAULT_BENEFITS));
  };

  // ----- Tier ops
  const updateTier = (i, patch) => {
    const next = clone(data);
    next.tiers[i] = { ...next.tiers[i], ...patch };
    commit(next);
  };

  // ----- Category ops
  const updateCategory = (ci, patch) => {
    const next = clone(data);
    next.categories[ci] = { ...next.categories[ci], ...patch };
    commit(next);
  };
  const addCategory = () => {
    const next = clone(data);
    next.categories.push({
      _cid: `c-${Math.random().toString(36).slice(2, 10)}`,
      key: `cat-${Date.now()}`, label_es: "Nueva categoría", label_fr: "Nouvelle catégorie", rows: [],
    });
    commit(next);
  };
  const removeCategory = (ci) => {
    if (!window.confirm("¿Eliminar esta categoría y todas sus filas?")) return;
    const next = clone(data);
    next.categories.splice(ci, 1);
    commit(next);
  };
  const moveCategory = (ci, dir) => {
    const next = clone(data);
    const tgt = ci + dir;
    if (tgt < 0 || tgt >= next.categories.length) return;
    [next.categories[ci], next.categories[tgt]] = [next.categories[tgt], next.categories[ci]];
    commit(next);
  };

  // ----- Row ops
  const addRow = (ci) => {
    const next = clone(data);
    next.categories[ci].rows.push({
      label_es: "Nueva ventaja", label_fr: "Nouvel avantage",
      cells: [{ type: "no" }, { type: "no" }, { type: "no" }],
    });
    commit(next);
  };
  const updateRow = (ci, ri, patch) => {
    const next = clone(data);
    next.categories[ci].rows[ri] = { ...next.categories[ci].rows[ri], ...patch };
    commit(next);
  };
  const removeRow = (ci, ri) => {
    const next = clone(data);
    next.categories[ci].rows.splice(ri, 1);
    commit(next);
  };
  const moveRow = (ci, ri, dir) => {
    const next = clone(data);
    const rows = next.categories[ci].rows;
    const tgt = ri + dir;
    if (tgt < 0 || tgt >= rows.length) return;
    [rows[ri], rows[tgt]] = [rows[tgt], rows[ri]];
    commit(next);
  };

  // ----- Cell ops
  const cycleCell = (ci, ri, cellIdx) => {
    const next = clone(data);
    const cell = next.categories[ci].rows[ri].cells[cellIdx];
    const nextType = cell.type === "yes" ? "no" : cell.type === "no" ? "text" : "yes";
    next.categories[ci].rows[ri].cells[cellIdx] = nextType === "text"
      ? { type: "text", es: cell.es || "", fr: cell.fr || "" }
      : { type: nextType };
    commit(next);
  };
  const updateCellText = (ci, ri, cellIdx, patch) => {
    const next = clone(data);
    next.categories[ci].rows[ri].cells[cellIdx] = {
      ...next.categories[ci].rows[ri].cells[cellIdx], ...patch,
    };
    commit(next);
  };

  return (
    <div data-testid="cms-settings-benefits">
      <SettingsHeader
        title="Ventajas del Socio"
        description="Edite las tres modalidades de socio, las categorías y las ventajas mostradas en /ventajas-socio."
        onSave={save}
        saving={saving}
        saved={saved}
        testId="benefits-save"
      />

      <div className="mb-6 flex justify-end">
        <button onClick={resetToDefault} className="text-xs text-gray-500 hover:text-[#C8102E]" data-testid="benefits-reset">
          Restablecer valores por defecto
        </button>
      </div>

      {/* ---- TIERS ---------------------------------------------------------- */}
      <Section title="Modalidades de socio (3 columnas)">
        <div className="grid md:grid-cols-3 gap-4">
          {data.tiers.map((t, i) => (
            <div key={t.key || `tier-${i}`} className={`p-4 border ${t.featured ? "border-[#C8102E] bg-[#FCEAED]" : "border-gray-200"}`} data-testid={`tier-editor-${i}`}>
              <div className="text-[10px] uppercase tracking-widest text-gray-500 mb-2">Columna #{i + 1}</div>
              <label className="text-xs text-gray-600">Clave interna</label>
              <input value={t.key} onChange={(e) => updateTier(i, { key: e.target.value })} className="w-full px-2 py-1.5 border border-gray-200 text-sm mb-2" />
              <label className="text-xs text-gray-600">Nombre (ES)</label>
              <input value={t.name_es} onChange={(e) => updateTier(i, { name_es: e.target.value })} className="w-full px-2 py-1.5 border border-gray-200 text-sm mb-2" />
              <label className="text-xs text-gray-600">Nombre (FR)</label>
              <input value={t.name_fr} onChange={(e) => updateTier(i, { name_fr: e.target.value })} className="w-full px-2 py-1.5 border border-gray-200 text-sm mb-2" />
              <label className="text-xs text-gray-600">Precio</label>
              <input value={t.price} onChange={(e) => updateTier(i, { price: e.target.value })} className="w-full px-2 py-1.5 border border-gray-200 text-sm mb-2" />
              <label className="flex items-center gap-2 text-xs mt-2">
                <input type="checkbox" checked={!!t.featured} onChange={(e) => updateTier(i, { featured: e.target.checked })} className="accent-[#C8102E]" />
                Destacar esta columna (fondo oscuro)
              </label>
            </div>
          ))}
        </div>
      </Section>

      {/* ---- CATEGORIES ---------------------------------------------------- */}
      <div className="mt-6 space-y-4">
        {data.categories.map((cat, ci) => (
          <div key={cat._cid || cat.key || `cat-${ci}`} className="bg-white border border-gray-200 p-5" data-testid={`cat-editor-${ci}`}>
            <div className="flex items-start gap-3 mb-4">
              <div className="flex-1 grid md:grid-cols-2 gap-3">
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1">Categoría (ES)</label>
                  <input value={cat.label_es} onChange={(e) => updateCategory(ci, { label_es: e.target.value })} className="w-full px-3 py-2 border border-gray-200 text-sm font-medium text-[#0F2040]" />
                </div>
                <div>
                  <label className="block text-[11px] uppercase tracking-wider text-gray-500 mb-1">Categoría (FR)</label>
                  <input value={cat.label_fr} onChange={(e) => updateCategory(ci, { label_fr: e.target.value })} className="w-full px-3 py-2 border border-gray-200 text-sm font-medium text-[#0F2040]" />
                </div>
              </div>
              <div className="flex gap-1 pt-5">
                <button onClick={() => moveCategory(ci, -1)} className="px-2 py-1.5 text-xs border border-gray-200 hover:border-[#0F2040]" title="Subir">↑</button>
                <button onClick={() => moveCategory(ci, +1)} className="px-2 py-1.5 text-xs border border-gray-200 hover:border-[#0F2040]" title="Bajar">↓</button>
                <button onClick={() => removeCategory(ci)} className="p-1.5 text-gray-400 hover:text-red-600"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>

            <table className="w-full text-sm border-collapse">
              <thead>
                <tr className="text-[10px] uppercase tracking-widest text-gray-500">
                  <th className="text-left px-2 py-1.5 w-2/5">Ventaja</th>
                  {data.tiers.map((t, i) => (<th key={t.key || `th-${i}`} className="text-center px-2 py-1.5 w-1/6">{t.name_es}</th>))}
                  <th className="w-24" />
                </tr>
              </thead>
              <tbody>
                {cat.rows.map((row, ri) => (
                  <tr key={row._rid || `row-${ci}-${ri}`} className="border-t border-gray-100" data-testid={`row-${ci}-${ri}`}>
                    <td className="px-2 py-1.5">
                      <input value={row.label_es} onChange={(e) => updateRow(ci, ri, { label_es: e.target.value })} className="w-full px-2 py-1 border border-gray-100 text-sm mb-1" placeholder="Etiqueta ES" />
                      <input value={row.label_fr} onChange={(e) => updateRow(ci, ri, { label_fr: e.target.value })} className="w-full px-2 py-1 border border-gray-100 text-xs text-gray-500 italic" placeholder="Étiquette FR" />
                    </td>
                    {row.cells.map((cell, cellIdx) => (
                      <td key={`${row._rid || ri}-c${cellIdx}`} className="px-2 py-1.5 text-center align-middle">
                        <button
                          onClick={() => cycleCell(ci, ri, cellIdx)}
                          className="inline-flex items-center justify-center w-8 h-8 border border-gray-200 hover:border-[#0F2040]"
                          title="Click para cambiar: sí → no → texto → sí"
                          data-testid={`cell-${ci}-${ri}-${cellIdx}`}
                        >
                          {cell.type === "yes" && <Check className="w-4 h-4 text-green-600" />}
                          {cell.type === "no" && <Minus className="w-4 h-4 text-gray-400" />}
                          {cell.type === "text" && <Type className="w-4 h-4 text-[#C8102E]" />}
                        </button>
                        {cell.type === "text" && (
                          <div className="mt-1 space-y-0.5">
                            <input value={cell.es || ""} onChange={(e) => updateCellText(ci, ri, cellIdx, { es: e.target.value })} className="w-full text-[11px] px-1 py-0.5 border border-gray-100" placeholder="ES" />
                            <input value={cell.fr || ""} onChange={(e) => updateCellText(ci, ri, cellIdx, { fr: e.target.value })} className="w-full text-[11px] px-1 py-0.5 border border-gray-100 italic" placeholder="FR" />
                          </div>
                        )}
                      </td>
                    ))}
                    <td className="text-right px-2 py-1.5 whitespace-nowrap">
                      <button onClick={() => moveRow(ci, ri, -1)} className="px-1.5 text-xs text-gray-400 hover:text-[#0F2040]">↑</button>
                      <button onClick={() => moveRow(ci, ri, +1)} className="px-1.5 text-xs text-gray-400 hover:text-[#0F2040]">↓</button>
                      <button onClick={() => removeRow(ci, ri)} className="p-1 text-gray-300 hover:text-red-600 ml-1"><Trash2 className="w-3.5 h-3.5" /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <button onClick={() => addRow(ci)} className="mt-3 inline-flex items-center gap-1.5 text-xs text-[#C8102E] hover:underline" data-testid={`add-row-${ci}`}>
              <Plus className="w-3.5 h-3.5" /> Añadir ventaja
            </button>
          </div>
        ))}
      </div>

      <button onClick={addCategory} className="mt-4 inline-flex items-center gap-2 text-sm border border-dashed border-[#C8102E] text-[#C8102E] px-4 py-2 hover:bg-[#FCEAED]" data-testid="add-category">
        <Plus className="w-4 h-4" /> Añadir categoría
      </button>
    </div>
  );
}
